import { ApiMetric } from "@incendium/api";
import { truncate } from "Helpers/truncate";
import { TChartData } from "Interfaces";
import { IYKey } from "Interfaces";
import useFormatMetric from "../features/analytics/hooks/useFormatMetric";
import { useMemo } from "react";
import { useUnmount } from "react-use";
import { XAxisProps, YAxisProps } from "recharts";
import { LayoutType } from "recharts/types/util/types";
import { DIMENSION_TRUNCATE } from "../features/analytics/constants";

const defaultFontSize = "11px";

const getMetricFromField = (field: string): ApiMetric =>
  (field.split("\\").pop() || field) as ApiMetric;

/** Cleans float noise from synthetic domain ticks before formatting. */
const snapDomainTick = (value: number): number => {
  if (!Number.isFinite(value)) {
    return 0;
  }
  const nearestInt = Math.round(value);
  if (Math.abs(value - nearestInt) < 1e-6) {
    return nearestInt;
  }
  return Math.round(value * 100) / 100;
};

// we calc the height by creating a hidden element with each tick name in it, see its width and calculate based on angle and a set height of 18px (tick height)
export const useXHeight = (
  layout: LayoutType,
  data: TChartData[],
  keys?: IYKey[] | string[], // Metric keys
  dimProps?: XAxisProps,
  metricProps?: YAxisProps[]
) => {
  const formatMetric = useFormatMetric();

  useUnmount(() => {
    const id = `inc-h-c-${names.join("-")}`;
    let el = document.getElementById(id);
    if (el) {
      el.remove();
    }
  });

  const names = useMemo(() => {
    return layout === "vertical"
      ? getMetricValues(data, keys, metricProps, formatMetric) // X-axis is metrics in vertical layout
      : getDimensionValues(data, dimProps); // X-axis is dimensions in horizontal layout
  }, [data, keys, dimProps, metricProps, layout, formatMetric]);

  return useMemo(() => {
    if (!names || names.length === 0) return 0;

    const height = getElWidth(
      layout,
      names,
      dimProps?.fontSize || defaultFontSize
    );

    let angleDegrees = dimProps?.angle || 0;
    let angleRadians = angleDegrees * (Math.PI / 180);
    // let projectedExtraHeight = height * Math.cos(angleRadians);
    let projectedHeight = height * Math.abs(Math.sin(angleRadians));
    return Math.max(projectedHeight + 12, 25);
  }, [layout, dimProps, names]);
};

export const useYWidth = (
  layout: LayoutType,
  data: TChartData[],
  keys?: IYKey[] | string[], // Metric keys
  dimProps?: XAxisProps,
  metricProps?: YAxisProps[]
) => {
  const formatMetric = useFormatMetric();

  const axisLabels = useMemo(() => {
    return layout === "vertical"
      ? [getDimensionValues(data, dimProps?.[0])] // Y-axis is dimensions in vertical layout
      : getYAxisTickLabels(data, keys, metricProps, formatMetric); // Y-axis is metrics in horizontal layout
  }, [data, keys, dimProps, metricProps, formatMetric, layout]);

  return useMemo(() => {
    if (!axisLabels || axisLabels.length === 0) return [0];

    return axisLabels.map((labels, i) => {
      if (!labels || labels.length === 0) return 40;

      return Math.max(
        getElWidth(
          layout,
          labels,
          metricProps?.[i]?.fontSize || defaultFontSize
        ) + 14,
        40
      );
    });
  }, [layout, axisLabels, metricProps]);
};

const sampleForMeasurement = <T,>(values: T[], maxSamples = 20): T[] => {
  if (values.length <= maxSamples) {
    return values;
  }

  const indices = new Set<number>([0, values.length - 1]);
  const step = Math.max(1, Math.floor(values.length / (maxSamples - 2)));
  for (let i = step; i < values.length - 1; i += step) {
    indices.add(i);
  }

  return Array.from(indices)
    .sort((a, b) => a - b)
    .map((i) => values[i]);
};

// Extracts dimension values from data
const getDimensionValues = (
  data: TChartData[],
  dimProps?: XAxisProps | YAxisProps // X or Y props for formatting
) => {
  const formatted = (data || []).map((d, i) => {
    const value = d.name;
    return dimProps?.tickFormatter
      ? dimProps.tickFormatter(value, i)
      : truncate(value, DIMENSION_TRUNCATE);
  });

  return sampleForMeasurement(formatted);
};

// Extracts metric values from data
const getMetricValues = (
  data: TChartData[],
  metricKeys?: IYKey[] | string[], // Metrics can be objects or strings
  metricProps?: YAxisProps[] | XAxisProps[], // X or Y props for formatting
  formatMetric?: (metric: ApiMetric, value: number) => string
) => {
  if (!metricKeys || !formatMetric) return [];

  return metricKeys
    .map((key, i) => {
      const prop = metricProps?.[i];

      if (typeof key === "object" && key.fields) {
        return key.fields
          .map((field) =>
            data.map((d) => {
              if (!d[field]) return "";
              const metric = getMetricFromField(field);
              return prop?.tickFormatter
                ? prop.tickFormatter(d[field], i)
                : formatMetric(metric, Number(d[field]));
            })
          )
          .flat();
      }

      return data.map((d) =>
        prop?.tickFormatter
          ? prop.tickFormatter(d[key], i)
          : formatMetric(key as ApiMetric, Number(d[key]))
      );
    })
    .flat();
};

// Generates formatted Y-axis tick labels using the same domain logic as StyledComposedChart
const getYAxisTickLabels = (
  data: TChartData[],
  metricKeys?: IYKey[] | string[],
  metricProps?: YAxisProps[],
  formatMetric?: (metric: ApiMetric, value: number) => string
): string[][] => {
  if (!metricKeys || !formatMetric) return [];

  return metricKeys.map((key, i) => {
    const prop = metricProps?.[i];
    const fields =
      typeof key === "object" && key.fields
        ? key.fields
        : [typeof key === "string" ? key : ""].filter(Boolean);

    const labels = new Set<string>();

    fields.forEach((field) => {
      const metric = getMetricFromField(field);
      const numericValues = data
        .map((d) => Number(d[field]))
        .filter((v) => !isNaN(v));

      if (numericValues.length === 0) return;

      const dataMin = Math.min(...numericValues);
      const dataMax = Math.max(...numericValues);
      const domainMin = snapDomainTick(dataMin < 0 ? dataMin * 1.2 : 0);
      const domainMax = snapDomainTick(dataMax === 0 ? 1 : dataMax * 1.2);

      const format = (v: number) => {
        const snapped = snapDomainTick(v);
        if (prop?.tickFormatter) {
          return prop.tickFormatter(snapped, i);
        }
        return formatMetric(metric, snapped);
      };

      const tickCount = 5;
      const step = (domainMax - domainMin) / (tickCount - 1);
      for (let t = 0; t < tickCount; t++) {
        labels.add(format(domainMin + step * t));
      }
    });

    return Array.from(labels);
  });
};

const measureElements = new Map<string, HTMLDivElement>();

const getElWidth = (
  layout: LayoutType,
  values: string[],
  fontSize?: number | string
) => {
  const resolvedFontSize = fontSize
    ? typeof fontSize === "string"
      ? fontSize
      : `${fontSize}px`
    : "11px";
  const cacheKey = `${layout}|${values.join("|")}|${resolvedFontSize}`;
  let el = measureElements.get(cacheKey);

  if (!el) {
    el = document.createElement("div");
    el.style.visibility = "hidden";
    el.style.height = "12px";
    el.style.overflow = "hidden";
    el.style.position = "absolute";
    el.style.zIndex = "-1";
    el.style.display = "inline-block";

    values.forEach((name) => {
      const innerElement = document.createElement("div");
      innerElement.innerHTML = name;
      innerElement.style.fontSize = resolvedFontSize;
      el!.appendChild(innerElement);
    });

    document.body.appendChild(el);
    measureElements.set(cacheKey, el);
  }

  return el.clientWidth;
};
